/**
 * D36.a -- Global Punch Pill in the top-bar.
 *
 * Shows current on-clock state (Off / In / WFH / Field). Click flips
 * punch state via the existing `/attendance/punch-in|out` endpoints.
 *
 * Every authed user sees it (positioned to the left of language toggle).
 * Refreshes on mount + on window focus. Bypasses `/hr/attendance` for
 * daily punches so the old muscle-memory "click into HR" step is gone.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogIn, LogOut, Compass, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/store/auth";
import { useIsCEO } from "@/hooks/useIsCEO";

const STATE_STYLE = {
  in:    "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
  out:   "bg-white/5 border-white/10 text-foreground/60",
  wfh:   "bg-sky-500/15 border-sky-500/40 text-sky-200",
  field: "bg-amber-500/15 border-amber-500/40 text-amber-200",
};

export default function GlobalPunchPill() {
  const user = useAuth((s) => s.user);
  const isCEO = useIsCEO();
  const [state, setState] = useState("out");   // in | out | wfh | field
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState(null);
  const [hover, setHover] = useState(false);

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [{ data: today }, { data: openTrip }] = await Promise.all([
        api.get("/attendance/today", { silent: true }),
        api.get("/attendance/field-log/open", { silent: true }).catch(() => ({ data: { open: false } })),
      ]);
      if (openTrip?.open) { setState("field"); return; }
      const punches = today?.attendance?.punches || [];
      const last = punches[punches.length - 1];
      const isIn = last && !last.out_time;
      const status = (today?.attendance?.status || "").toLowerCase();
      if (status === "wfh" && isIn) setState("wfh");
      else if (isIn) setState("in");
      else setState("out");
    } catch { /* silent -- pill is best-effort */ }
  }, [user?.id]);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  useEffect(() => {
    if (!navigator.geolocation) return undefined;
    const id = navigator.geolocation.watchPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }),
      () => setPos(null),
      { enableHighAccuracy: false, maximumAge: 60000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  const punch = async () => {
    if (!user?.id || state === "field") return;
    if (!pos) {
      toast.error("Location required to punch. Enable geolocation.");
      // D36.b -- log the failed attempt for pill telemetry.
      api.post("/hr/attendance/pill-log", {
        action: "attempt", from_page: window.location.pathname,
        outcome: "no_geo",
      }, { silent: true }).catch(() => {});
      return;
    }
    setBusy(true);
    const kind = state === "out" ? "punch-in" : "punch-out";
    try {
      await api.post(`/attendance/${kind}`,
                     { lat: pos.lat, lng: pos.lng, accuracy: pos.acc });
      toast.success(kind === "punch-in" ? "Punched in" : "Punched out");
      // D36.b -- pill telemetry (Direction B validation).
      api.post("/hr/attendance/pill-log", {
        action: kind === "punch-in" ? "in" : "out",
        from_page: window.location.pathname,
        outcome: "success",
      }, { silent: true }).catch(() => {});
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Punch failed";
      toast.error(String(msg));
      api.post("/hr/attendance/pill-log", {
        action: "attempt", from_page: window.location.pathname,
        outcome: "error",
      }, { silent: true }).catch(() => {});
    } finally { setBusy(false); }
  };

  if (!user?.id) return null;
  // D36.c -- CEO is not tracked in attendance; hide the pill entirely.
  // D54-d Group F -- migrated from `user.role === "CEO"` to
  // `useIsCEO()` matrix consult.
  if (isCEO) return null;

  const labelMap = { in: "IN", out: "OUT", wfh: "WFH", field: "FIELD" };
  const ico = state === "field"
    ? <Compass className="h-3.5 w-3.5" />
    : state === "out"
      ? <LogIn className="h-3.5 w-3.5" />
      : <LogOut className="h-3.5 w-3.5" />;

  return (
    <button
      onClick={punch}
      disabled={busy || state === "field"}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid="global-punch-pill"
      data-punch-state={state}
      title={state === "field"
        ? "You're on a field trip -- close it from Attendance Hub"
        : state === "out"
          ? "Click to punch in"
          : "Click to punch out"}
      className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border text-[11px] uppercase tracking-widest transition ${STATE_STYLE[state] || ""} ${state === "field" ? "cursor-not-allowed" : "hover:brightness-125"}`}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : ico}
      <span>{labelMap[state]}</span>
      {state !== "field" && hover && !busy && (
        <span className="opacity-70 hidden sm:inline">
          {state === "out" ? "· click to punch in" : "· click to punch out"}
        </span>
      )}
    </button>
  );
}
